import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck } from "lucide-react";
import { BROKER } from "../lib/data";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-aurora pt-32 pb-20 lg:pt-44 lg:pb-32"
    >
      {/* Futuristic grid pattern */}
      <div className="absolute inset-0 grid-overlay opacity-50" />

      {/* Floating gold orbs */}
      <div className="absolute top-32 right-10 w-72 h-72 bg-gold/15 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-10 left-1/4 w-96 h-96 bg-gold-light/10 rounded-full blur-3xl" />

      {/* Top accent */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-px bg-gold" />
            <span className="eyebrow text-gold">Independent Insurance Broker</span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.05] tracking-tight text-ink font-light">
            Protecting <span className="italic shimmer">Families.</span>
            <br />
            Securing <span className="italic gold-text-gradient">Legacies.</span>
          </h1>

          <p className="mt-10 text-lg sm:text-xl text-ink/65 leading-relaxed font-light max-w-2xl">
            One conversation today can shield the people you love from the financial weight of an
            unimaginable tomorrow. Tailored life insurance from twelve of the nation's most trusted
            carriers — personally curated by {BROKER.name}.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-quote-cta"
              className="group relative inline-flex items-center justify-center gap-3 bg-ink text-gold-light px-8 py-4 text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden transition-all duration-500 hover:text-ink"
            >
              <span className="absolute inset-0 bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              <span className="relative">Get a Free Quote</span>
              <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={BROKER.phoneLink}
              data-testid="hero-call-cta"
              className="group inline-flex items-center justify-center gap-3 border border-ink/30 text-ink px-8 py-4 text-sm font-semibold tracking-[0.2em] uppercase hover:border-gold hover:bg-gold/10 transition-all duration-500"
            >
              <Phone className="w-4 h-4" />
              Call {BROKER.phone}
            </a>
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-ink/55 text-xs tracking-[0.25em] uppercase">
            <div className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-gold" /> Licensed & Bonded</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> NPN {BROKER.npn}</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> Ages 0–89 Eligible</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> 12 Trusted Carriers</div>
          </div>
        </motion.div>
      </div>

      {/* Bottom gradient fade to next section */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-cream/40 pointer-events-none" />
    </section>
  );
};

export default Hero;
